// @givmo/app-admin — the reserved assistant entry-point slot (EP-DES2 Wave 0; spec §7).
//
// EP-AI1 puts ONE persistent assistant entry in the top bar beside Help, opening a right-side panel
// scoped to the current surface. That program is late, but retrofitting a slot into a frozen shell
// is expensive — so Wave 0 reserves the POSITION and the REGISTRATION SEAM now, and the shell never
// has to be reopened to accept it.
//
// It renders NOTHING until something registers. Deliberately not a hidden icon and not a disabled
// button: a control that looks wired and goes nowhere is the exact §5 hygiene failure this wave
// closes. With nothing registered the slot emits no DOM at all.
//
// EP-AI1 registers once, at module init of the assistant feature:
//     registerAssistantEntryPoint(AssistantTopBarButton);
// and may pass `null` to withdraw it (a kill switch, or a flag flipping off).

import { useSyncExternalStore, type ComponentType } from 'react';

/** A registered entry point renders the whole control — icon, label, and its own panel trigger. */
export type AssistantEntryPoint = ComponentType;

let entryPoint: AssistantEntryPoint | null = null;
const subscribers = new Set<() => void>();

/** Register (or, with `null`, withdraw) the top-bar assistant entry point. */
export function registerAssistantEntryPoint(component: AssistantEntryPoint | null): void {
  entryPoint = component;
  for (const notify of subscribers) notify();
}

function subscribe(notify: () => void): () => void {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}

function getSnapshot(): AssistantEntryPoint | null {
  return entryPoint;
}

/**
 * The reserved slot itself, mounted in the shell's top bar beside Help. Subscribing (rather than
 * reading the module variable once) means a registration that lands after the shell mounted still
 * shows up without a reload.
 */
export function AssistantEntryPointSlot() {
  const Entry = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return Entry ? <Entry /> : null;
}
